import { FormControlLabel, MenuItem, Switch, TextField } from '@mui/material';
import { useState } from 'react';

import { createVariant, updateVariant } from '../../api/business/endpoints';
import type { ProductDetail, Variant, VariantInput } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { formatNumber } from '../../components/format';
import {
  moneyInputValue,
  parseMoneyInput,
  parseOptionalWholeNumber,
  parseUnitQuantity,
  unitQuantityInputValue,
} from '../../components/parse';
import { useLanguage } from '../../i18n/LanguageContext';

type VariantDialogProps = {
  companyId: string;
  currency: string;
  product: ProductDetail;
  /** null adds a variant; a variant edits it. */
  variant: Variant | null;
  onClose: () => void;
};

/**
 * A variant: a value for each option the product varies by, its own codes, price and weight (ADR-0010 rules 3,
 * 5, 6; R0030 WP-05) and, for a product with a unit price, its own quantity of the unit (WP-07).
 * The API checks the combination is new and the codes are free.
 */
export const VariantDialog = ({ companyId, currency, product, variant, onClose }: VariantDialogProps) => {
  const { language, t } = useLanguage();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((variant?.optionValues ?? []).map((value) => [value.groupId, value.valueId])),
  );
  const [sku, setSku] = useState(variant?.sku ?? '');
  const [ean, setEan] = useState(variant?.ean ?? '');
  const [price, setPrice] = useState(moneyInputValue(variant?.priceMinor ?? null));
  const [mpn, setMpn] = useState(variant?.mpn ?? '');
  const [weight, setWeight] = useState(
    variant?.weightGrams === null || !variant ? '' : String(variant.weightGrams),
  );
  const [unitQuantity, setUnitQuantity] = useState(unitQuantityInputValue(variant?.unitQuantity ?? null));
  const [isDefault, setIsDefault] = useState(variant?.isDefault ?? false);
  const [problem, setProblem] = useState<string | null>(null);

  const mutation = useAppMutation(
    (input: VariantInput) =>
      variant
        ? updateVariant(companyId, product.id, variant.id, input)
        : createVariant(companyId, product.id, input),
    ['product', 'products'],
    onClose,
  );

  const submit = (): void => {
    const priceMinor = parseMoneyInput(price);
    const optionValueIds = product.optionGroups.map((group) => values[group.id]).filter(Boolean);

    const weightGrams = parseOptionalWholeNumber(weight);
    const ownUnitQuantity = product.unitLabel === null ? null : parseUnitQuantity(unitQuantity);

    if (priceMinor === undefined) {
      setProblem(t('money.invalid'));

      return;
    }

    if (weightGrams === undefined) {
      setProblem(t('variants.weightInvalid'));

      return;
    }

    if (ownUnitQuantity === undefined) {
      setProblem(t('legal.unitQuantityInvalid'));

      return;
    }

    if (optionValueIds.length !== product.optionGroups.length) {
      setProblem(t('variants.valueRequired'));

      return;
    }

    setProblem(null);
    mutation.mutate(
      variant
        ? {
            optionValueIds,
            sku: sku.trim() || null,
            ean: ean.trim() || null,
            mpn: mpn.trim() || null,
            priceMinor,
            weightGrams,
            ...(product.unitLabel === null ? {} : { unitQuantity: ownUnitQuantity }),
            ...(isDefault && !variant.isDefault ? { isDefault: true } : {}),
          }
        : {
            optionValueIds,
            sku: sku.trim() || undefined,
            ean: ean.trim() || undefined,
            mpn: mpn.trim() || undefined,
            priceMinor: priceMinor ?? undefined,
            weightGrams: weightGrams ?? undefined,
            unitQuantity: ownUnitQuantity ?? undefined,
            isDefault,
          },
    );
  };

  return (
    <FormDialog
      title={variant ? t('variants.edit') : t('variants.create')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={variant ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending}
      error={mutation.error}
      problem={problem}
    >
      {product.optionGroups.map((group, index) => (
        <TextField
          key={group.id}
          select
          required
          autoFocus={index === 0}
          label={group.name}
          value={values[group.id] ?? ''}
          onChange={(e) => setValues({ ...values, [group.id]: e.target.value })}
        >
          {group.values.map((value) => (
            <MenuItem key={value.id} value={value.id}>
              {value.value}
            </MenuItem>
          ))}
        </TextField>
      ))}
      <TextField label={t('field.sku')} value={sku} onChange={(e) => setSku(e.target.value)} />
      <TextField
        label={t('field.ean')}
        helperText={t('products.eanHint')}
        value={ean}
        inputProps={{ inputMode: 'numeric', maxLength: 14 }}
        onChange={(e) => setEan(e.target.value)}
      />
      <TextField
        label={t('field.mpn')}
        value={mpn}
        inputProps={{ maxLength: 64 }}
        onChange={(e) => setMpn(e.target.value)}
      />
      <TextField
        label={t('field.weightGrams')}
        helperText={t('variants.weightHint')}
        value={weight}
        inputProps={{ inputMode: 'numeric' }}
        onChange={(e) => setWeight(e.target.value)}
      />
      {product.unitLabel !== null && product.unitQuantity !== null ? (
        <TextField
          label={t('legal.variantUnitQuantity', { unit: t(`unitSymbol.${product.unitLabel}`) })}
          helperText={t('legal.variantUnitQuantityHint', {
            quantity: formatNumber(product.unitQuantity, language),
            unit: t(`unitSymbol.${product.unitLabel}`),
          })}
          value={unitQuantity}
          inputProps={{ inputMode: 'decimal' }}
          onChange={(e) => setUnitQuantity(e.target.value)}
        />
      ) : null}
      <TextField
        label={t('variants.price', { currency })}
        helperText={t('variants.priceHint')}
        value={price}
        inputProps={{ inputMode: 'decimal' }}
        onChange={(e) => setPrice(e.target.value)}
      />
      <FormControlLabel
        control={
          <Switch
            checked={isDefault}
            disabled={variant?.isDefault === true || variant?.status === 'ARCHIVED'}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
        }
        label={t('variants.makeDefault')}
      />
    </FormDialog>
  );
};
